// Theme-related type definitions and constants

// Core theme types - logical concepts without implementation details
export type ThemeColor = 'light' | 'sepia' | 'storm' | 'dark';
export type ThemeFont =
  | 'lyon'
  | 'tisa'
  | 'literata'
  | 'georgia'
  | 'open-sans'
  | 'helvetica'
  | 'san-francisco'
  | 'atkinson'
  | 'opendyslexic';
export type FontGroup = 'serif' | 'sans' | 'accessibility';
export type ThemeSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
export type ThemeWidth = 'sm' | 'md' | 'lg' | 'xl';
export type ThemeJustify = 'start' | 'justify';
export type ReaderSidebarMode = 'highlights' | 'summary' | 'notes';

// Theme constants - single source of truth with stable order
export const THEME_COLORS: readonly ThemeColor[] = [
  'light',
  'sepia',
  'storm',
  'dark',
] as const;
export const THEME_FONTS: readonly ThemeFont[] = [
  'lyon',
  'tisa',
  'literata',
  'georgia',
  'open-sans',
  'helvetica',
  'san-francisco',
  'atkinson',
  'opendyslexic',
] as const;

export interface FontOption {
  key: ThemeFont;
  label: string;
  /** Shown on the collapsed font row when the full name would overflow. */
  shortLabel?: string;
  group: FontGroup;
  /** Hidden where the system font is unavailable. */
  appleOnly?: boolean;
}

export const FONT_OPTIONS: readonly FontOption[] = [
  { key: 'lyon', label: 'Lyon', group: 'serif' },
  { key: 'tisa', label: 'Tisa', group: 'serif' },
  { key: 'literata', label: 'Literata', group: 'serif' },
  { key: 'georgia', label: 'Georgia', group: 'serif' },
  { key: 'open-sans', label: 'Open Sans', group: 'sans' },
  { key: 'helvetica', label: 'Helvetica', group: 'sans' },
  { key: 'san-francisco', label: 'San Francisco', group: 'sans', appleOnly: true },
  {
    key: 'atkinson',
    label: 'Atkinson Hyperlegible',
    shortLabel: 'Atkinson',
    group: 'accessibility',
  },
  { key: 'opendyslexic', label: 'Dyslexic', group: 'accessibility' },
] as const;

const LEGACY_THEME_FONTS: Record<string, ThemeFont> = {
  elena: 'tisa',
  'new-york': 'georgia',
  'ideal-sans': 'helvetica',
  'proxima-nova': 'open-sans',
};

export function normalizeThemeFont(font: unknown): ThemeFont {
  if (typeof font === 'string' && (THEME_FONTS as readonly string[]).includes(font)) {
    return font as ThemeFont;
  }
  if (typeof font === 'string' && font in LEGACY_THEME_FONTS) {
    return LEGACY_THEME_FONTS[font];
  }
  return 'lyon';
}

function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPad|iPhone|iPod/.test(navigator.platform);
}

export function visibleFontOptions(
  isMac: boolean = isApplePlatform(),
): readonly FontOption[] {
  return FONT_OPTIONS.filter((option) => isMac || !option.appleOnly);
}

export function resolveVisibleFont(
  font: ThemeFont,
  isMac: boolean = isApplePlatform(),
): ThemeFont {
  if (!isMac && font === 'san-francisco') return 'open-sans';
  return font;
}

export const THEME_SIZES: readonly ThemeSize[] = [
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
  'xxl',
] as const;
export const THEME_WIDTHS: readonly ThemeWidth[] = ['sm', 'md', 'lg', 'xl'] as const;
export const THEME_JUSTIFIES: readonly ThemeJustify[] = ['start', 'justify'] as const;
export const READER_SIDEBAR_MODES: readonly ReaderSidebarMode[] = [
  'highlights',
  'summary',
  'notes',
] as const;

// CSS implementation types - how themes are applied
export type ThemeBodyClass = `theme-color-${ThemeColor}`;
export type FontBodyClass = `read-font-${ThemeFont}`;
export type SizeBodyClass = `read-text-${ThemeSize}`;
export type WidthBodyClass = `read-width-${ThemeWidth}`;
export type JustifyBodyClass = `read-align-${ThemeJustify}`;

// Helper functions for converting logical themes to CSS classes
export function getThemeBodyClass(theme: ThemeColor): ThemeBodyClass {
  return `theme-color-${theme}`;
}

export function getFontBodyClass(font: ThemeFont): FontBodyClass {
  return `read-font-${font}`;
}

export function getSizeBodyClass(size: ThemeSize): SizeBodyClass {
  return `read-text-${size}`;
}

export function getWidthBodyClass(width: ThemeWidth): WidthBodyClass {
  return `read-width-${width}`;
}

export function getJustifyBodyClass(justify: ThemeJustify): JustifyBodyClass {
  return `read-align-${justify}`;
}

// Legacy compatibility types (for existing code that uses the old format)
export type FontMode = ThemeFont;
export type WidthMode = ThemeWidth;

// Utility type for nullable values
type Nullable<T> = T | undefined | null;

// Theme configuration interface
export interface ThemeConfig {
  color: ThemeBodyClass;
  font: FontMode;
  size: ThemeSize;
  width: WidthMode;
  justify: ThemeJustify;
  wpm: number;
}

// Theme settings interface for user preferences
export interface ThemeSettings extends ThemeConfig {
  showReaderSidebar: boolean;
  readerSidebarMode: Nullable<ReaderSidebarMode>;
}
